import ApiClient from '@/api/ApiClient';
import Course from '@/api/models/Course';
import CourseModule from '@/api/models/CourseModule';
import store from '@/store';
import BaseService from './BaseService';
import Category from '@/api/models/Category';
import Language from '@/api/models/Language';
import { env } from '@/composables/config';
import UseStoreHandler from '@/composables/handlers/UseStoreHandler';

class CourseService extends BaseService {
    #prefixUrl = 'api/course/';

    STORAGE_DEFINITION = {
        MOST_RATED: 'mostRated',
        MOST_SOLD: 'mostSold',
        COURSE_DATA: 'courseData_${uuid}'
    };

    constructor() {
        super();
        this.api = new ApiClient(this.#prefixUrl);
    }

    /**
     * Gets all of logged user courses
     * Note: Related to student courses
     * @returns {Array} 
     */
    async getLoggedStudentCourses() {
        const response = await this.api.executeRequest('/mycourses', {}, this.api.methods.GET);
        let myCourses = {
            courses: [],
            byUuid: {},
            byName: {},
            byStatus: {
                complete: [],
                incomplete: []
            }
        }
        if (response.success()) {
            for (let course of response.getContent()) {
                const courseObj = new Course(course);
                myCourses.courses.push(courseObj);
                myCourses.byUuid[courseObj.uuid] = courseObj;
                myCourses.byName[courseObj.title] = courseObj;
                myCourses.byStatus[courseObj.status].push(courseObj);

                store.dispatch('saveOnDataChest', {
                    key: `couse_${courseObj.uuid}`,
                    value: courseObj
                });
            }
        }

        response.setContent(myCourses);
        return response;
    }

    /**
     * Get course details
     * @param {String} uuid
     * @returns {Course|null}
     */
    async showCourse(uuid = '') {
        const response = await this.api.executeRequest(`/show/${uuid}`, {}, this.api.methods.GET);
        response.setContent(new Course(response.getContent()));
        return response;
    }

    /**
     * Gets course modules
     * @param {String} courseUuid 
     * @returns {Array}
     */
    async getCourseModules(courseUuid = '') {
        const response = await this.api.executeRequest(`/modules/${courseUuid}`, {}, this.api.methods.GET);
        let courseModules = [];
        if (response.success()) {
            const data = response.getContent();
            for (let cm of Object.keys(data)) {
                courseModules.push(new CourseModule(data[cm]));
            }
        }
        response.setContent(courseModules);
        return response;
    }

    /**
     * Gets course modules and videos of Course
     * @param {String} courseUuid 
     * @returns {Array}
     */
    async getSimplifiedCourseModules(courseUuid = '') {
        const response = await this.api.executeRequest(`/display/modules/${courseUuid}`, {}, this.api.methods.GET);
        let courseModules = [];
        if (response.success()) {
            const data = response.getContent();
            for (let cm of Object.keys(data)) {
                courseModules.push(new CourseModule(data[cm]));
            }
        }
        response.setContent(courseModules);
        return response;
    }

    /**
     * Gets courses by uuid
     * @param {Array} courseUuids 
     * @returns {Response}
     */
    async getCoursesByUuid(courseUuids = []) {
        const response = await this.api.executeRequest(`/fetch`, {uuids: courseUuids}, this.api.methods.GET);
        let courses = [];
        if (response.success()) {
            const data = response.getContent();
            for (let c of Object.keys(data)) {
                courses.push(new Course(data[c]));
            }
        }
        response.setContent(courses);
        return response;
    }

    /**
     * Searches for courses by free term
     * @param {String} term 
     * @returns 
     */
    async searchCourse(term = '') {
        const response = await this.api.executeRequest(`/search`, {term: term}, this.api.methods.GET);
        let courses = [];
        if (response.success()) {
            const data = response.getContent();
            if (data.length > 0) {
                for (let c of Object.keys(data)) {
                    courses.push(new Course(data[c]));
                }
            }
        }
        response.setContent(courses);
        return response;
    }

    /**
     * Gets course statistics
     * @param {String} courseUuid 
     * @returns {Response}
     */
    async getStatistics(courseUuid = '', sumarizeData = false) {
        const response = await this.api.executeRequest(`/statistics/${courseUuid}`, {sumarize: sumarizeData}, this.api.methods.GET);
        let course = null;
        if (response.success()) {
            course = new Course(response.getContent());
        }
        response.setContent(course);
        return response;
    }

    /**
     * Get all active courses
     * @returns {Array}
     */
    async activeCourses() {
        const response = await this.api.executeRequest(`/index`, {}, this.api.methods.GET);
        
        let courses = [];
        if (response.success()) {
            for (let courseData of response.getContent().data) {
                courses.push(new Course(courseData));
            }
        }

        return courses;
    }

    /**
     * Get all active courses
     * @param {Object} filters
     * @returns {Array}
     */
    async searchCourseWithFilters(filters = {}) {
        const response = await this.api.executeRequest(`/simple_search`, filters, this.api.methods.GET);
        
        let courses = [];

        if (response.success()) {
            for (let courseData of response.getContent()) {
                courses.push(new Course(courseData));
            }
        }

        return courses;
    }

    /**
     * Get most sold courses
     * @returns {Array}
     */
    async mostSold(tag = null, limit = 10) {
        let coursesList = this.getArrayOnStorage(this.STORAGE_DEFINITION.MOST_SOLD);

        if (coursesList.length == 0) {
            const response = await this.api.executeRequest(`/mostsold`, {tag: tag, limit: limit}, this.api.methods.GET);
            
            if (response.success()) {
                coursesList = response.getContent().map(courseData => new Course(courseData));
                this.saveArrayOnStorage(this.STORAGE_DEFINITION.MOST_SOLD, coursesList);
            }

            this.saveArrayOnStorage(this.STORAGE_DEFINITION.MOST_SOLD, coursesList);
        }
        
        return coursesList;
    }

    /**
     * Get most rated courses
     * @returns {Array}
     */
    async mostRated(tag = null, limit = 10) {
        let coursesList = this.getArrayOnStorage(this.STORAGE_DEFINITION.MOST_SOLD);

        if (!coursesList || coursesList.length == 0) {
            const response = await this.api.executeRequest(`/mostrated`, {tag: tag, limit: limit}, this.api.methods.GET);
            
            if (response.success()) {
                coursesList = response.getContent().map(courseData => new Course(courseData));
                this.saveArrayOnStorage(this.STORAGE_DEFINITION.MOST_SOLD, coursesList);
            }

        }

        coursesList = await this.filterCoursesByDebug(coursesList);
        
        return coursesList;
    }

    /**
     * Get admin courses
     * @returns {Array}
     */
    async getAdminCourses() {
        let coursesList = [];

        const response = await this.api.executeRequest(`/courses`, {}, this.api.methods.GET);

        if (response.success()) {
            for (let courseData of response.getContent()) {
                coursesList.push(new Course(courseData));
            }
        }
        
        return coursesList;
    }

    /**
     * Saves course by creating or updating
     * Obs: if uuid is sent, updates course
     * @param {object} courseData
     * @param {string} uuid - if sent updates course
     * @returns {api/models/Response}
     */
    async saveCourse(courseData = {}, uuid = null) {
        let response = false;

        if (uuid) {
            response = await this.api.executeRequest(`/${uuid}`, courseData, this.api.methods.PUT);
        } else {
            response = await this.api.executeRequest('/', courseData, this.api.methods.POST);
        }
        
        return response;
    }

    /**
     * Gets admin course modules data
     * @param {String} courseUuid 
     * @returns {Array}
     */
    async getCourseModulesData(courseUuid = '') {
        const response = await this.api.executeRequest(`/modulesdata/${courseUuid}`, {}, this.api.methods.GET);

        let courseModules = [];

        if (response.success()) {
            const data = response.getContent();
            
            for (let cm of Object.keys(data)) {
                courseModules.push(new CourseModule(data[cm]));
            }
        }

        return courseModules;
    }

    /**
     * Storages sent course data on StorageStore
     * @param {string} uuid 
     * @param {*} data 
     * @returns {bool}
     */
    storageCourseData(uuid = null, data = []) {
        if (!uuid) {
            return false;
        }

        localStorage.setItem(this.STORAGE_DEFINITION.COURSE_DATA.replace('${uuid}', uuid), JSON.stringify([data]));
        return this.saveArrayOnStorage(this.STORAGE_DEFINITION.COURSE_DATA.replace('${uuid}', uuid), [data]);
    }

    /**
     * Gets item from storage
     * @param {string} uuid 
     * @returns {*}
     */
    getStoragedCourseData(uuid = null) {
        let data = this.getArrayOnStorage(this.STORAGE_DEFINITION.COURSE_DATA.replace('${uuid}', uuid));

        if (Array.isArray(data) && data.length == 0) {
            data = JSON.parse(localStorage.getItem(this.STORAGE_DEFINITION.COURSE_DATA.replace('${uuid}', uuid)));
        }

        return Array.isArray(data) && data.length > 0 ? data[0] : null;
    }

    /**
     * Saves Course categories
     * @param {string} courseUuid
     * @param {Array} categoriesIds
     * @returns {Response} 
     */
    async saveCategory(courseUuid = null, categoriesIds = []) {
        const response = await this.api.executeRequest(`/save_categories/${courseUuid}`, {categories: categoriesIds}, this.api.methods.POST);
        return response;
    }

    /**
     * Gets Couser filters
     * @returns {
     *  languages: Array,
     *  categories: Array
     * }
     */
    async getFiltersData() {
        const response = await this.api.executeRequest('/filters', {}, this.api.methods.GET);

        let responseObject = {
            languages: [],
            categories: []
        }

        if (response.success()) {
            const data = response.getContent();

            data.languages.forEach((lang) => {
                responseObject.languages.push(new Language(lang));
            });

            data.categories.forEach((category) => {
                responseObject.categories.push(new Category(category));
            });
        }

        return responseObject;
    }

    /**
     * Gets defined sort options
     * @returns {Array}
     */
    getSortOptions() {
        const course = new Course({});
        return course.VALID_SORTS_ON_SEARCH;
    }

    /**
     * Checks if course uuid is User's Course
     * @param {String} courseUuid
     * @returns {Boolean}
     */
    async isMyCourse(courseUuid = null) {
        const response = await this.api.executeRequest(`/is_my_course/${courseUuid}`, {}, this.api.methods.GET);
        return response.success();
    }

    /**
     * Removes (inactive) course
     * @param {String} courseUuid
     * @returns {Boolean}
     */
    async removeCourse(courseUuid = null) {
        const response = await this.api.executeRequest(`/inactive/${courseUuid}`, {}, this.api.methods.GET);
        return response.success();
    }

    /**
     * Filter courses for DEBUG.
     * @param {Array} data 
     * @returns {Array}
     */
    async filterCoursesByDebug(data = []) {
        const allowedUserStr = env('APP_ALLOWED_USER_IDS_FOR_COURSE');
        const allowedCourseStr = env('APP_ALLOWED_COURSES');

        const allowedUserIds = allowedUserStr ? allowedUserStr.split(',').map(Number) : [];
        const allowedCourseIds = allowedCourseStr ? allowedCourseStr.split(',').map(Number) : [];

        if (allowedUserIds.length === 0 && allowedCourseIds.length === 0) {
            return data;
        }

        const authUser = UseStoreHandler.getAuthStore();
        const userId = authUser.isAuthenticated ? authUser.getUserId : false;
        
        let filtered = [];
        for (let course of data) {
            if (!allowedCourseIds.includes(course.id)) {
                filtered.push(course);
                continue;
            }

            const checkCourse = allowedCourseIds.includes(course.id);

            if (
                checkCourse &&
                ( !userId || !allowedUserIds.includes(userId) )
            ) {
                continue;
            }
            
            filtered.push(course);
        }

        return filtered;
    }
}

export default new CourseService();
